/**
 * THE TOUR DEFINITION (KPR-611, KPE-024). Documented in docs/ARCHITECTURE.md
 * § "Product tours"; the rules for how one reads are in
 * docs/platform-standards.md § 16.
 *
 * A tour is a typed definition that ships with the UI it points at: a file
 * under `src/tours/definitions/`, reached only through `loadTour`
 * (`./loadTour.js`). It is plain data apart from a step's `condition`.
 *
 * @typedef {'info'|'click'|'type'|'navigate'} StepKind
 *   info      the person presses Next
 *   click     waits until the person really clicks the target
 *   type      waits until the target (or the field inside it) holds a value
 *   navigate  waits until the app is on `route`
 *
 * @typedef {'new'|'existing'|'everyone'} TourAudience
 *   who the tour is for. The engine does not filter by it: the caller that
 *   offers a tour (the Home card, the Help menu) decides, and a progress
 *   record can say who has seen which version.
 *
 * @typedef {object} TourStep
 * @property {string} [id]        unique inside the tour; defaults to `step-<n>`
 * @property {string} [target]    the value of a `data-tour` attribute on a real
 *                                element (see `tourTarget.js`). None: a centred
 *                                message. A `click` or `type` step needs one.
 * @property {string} [page]      the rendered page the target belongs to
 *                                (defaults to the tour's `page`); the
 *                                target-existence test renders it
 * @property {string} title       short; sentence case
 * @property {string} text        one or two sentences
 * @property {StepKind} kind
 * @property {string} [route]     for `navigate`: the react-router pattern to wait for
 * @property {string} [hint]      replaces the line the engine says for an action step
 * @property {(context: object) => boolean} [condition]  skipped when it returns false
 *                                or throws; `context` is `{ pathname, ...startContext }`
 *
 * @typedef {object} Tour
 * @property {string} id          stable; progress is kept per id
 * @property {number} version     a positive integer; raise it when the tour changes
 * @property {string} title
 * @property {TourAudience} audience
 * @property {string} [page]      default `page` for the steps
 * @property {TourStep[]} steps
 */

export const STEP_KINDS = Object.freeze(['info', 'click', 'type', 'navigate']);
export const AUDIENCES = Object.freeze(['new', 'existing', 'everyone']);

/** Kinds that wait for something the person does, not for Next. */
export const ACTION_KINDS = Object.freeze(['click', 'type', 'navigate']);

const NAME_PATTERN = /^[a-z0-9][a-z0-9-]*$/;

const isText = (value) => typeof value === 'string' && value.trim() !== '';
const isName = (value) => typeof value === 'string' && NAME_PATTERN.test(value);

/** Is this kind one the person completes by acting? */
export const isActionKind = (kind) => ACTION_KINDS.includes(kind);

/**
 * What is wrong with a definition, as sentences. Empty means it is sound.
 * Pure: it never throws and never touches the DOM.
 * @param {unknown} tour
 * @returns {string[]}
 */
export function validateTour(tour) {
	const problems = [];
	if (!tour || typeof tour !== 'object' || Array.isArray(tour)) return ['A tour is an object.'];
	const label = typeof tour.id === 'string' && tour.id ? `Tour "${tour.id}"` : 'Tour';
	if (!isName(tour.id)) problems.push(`${label}: id must be lowercase words joined by hyphens.`);
	if (!Number.isInteger(tour.version) || tour.version < 1) problems.push(`${label}: version must be a positive integer.`);
	if (!isText(tour.title)) problems.push(`${label}: title is required.`);
	if (!AUDIENCES.includes(tour.audience)) problems.push(`${label}: audience must be one of ${AUDIENCES.join(', ')}.`);
	if (tour.page !== undefined && !isName(tour.page)) problems.push(`${label}: page must be a name.`);
	if (!Array.isArray(tour.steps) || tour.steps.length === 0) {
		problems.push(`${label}: steps must be a non-empty list.`);
		return problems;
	}
	const seen = new Set();
	tour.steps.forEach((step, index) => {
		const where = `${label}, step ${index + 1}`;
		if (!step || typeof step !== 'object' || Array.isArray(step)) {
			problems.push(`${where}: a step is an object.`);
			return;
		}
		const id = step.id === undefined ? `step-${index + 1}` : step.id;
		if (!isName(id)) problems.push(`${where}: id must be lowercase words joined by hyphens.`);
		else if (seen.has(id)) problems.push(`${where}: id "${id}" is used twice.`);
		seen.add(id);
		if (!isText(step.title)) problems.push(`${where}: title is required.`);
		if (!isText(step.text)) problems.push(`${where}: text is required.`);
		if (!STEP_KINDS.includes(step.kind)) problems.push(`${where}: kind must be one of ${STEP_KINDS.join(', ')}.`);
		if (step.target !== undefined && !isName(step.target)) {
			problems.push(`${where}: target must be a data-tour value (lowercase words joined by hyphens).`);
		}
		if ((step.kind === 'click' || step.kind === 'type') && step.target === undefined) {
			problems.push(`${where}: a ${step.kind} step needs a target.`);
		}
		if (step.kind === 'navigate' && !isText(step.route)) problems.push(`${where}: a navigate step needs a route.`);
		if (step.route !== undefined && step.kind !== 'navigate') problems.push(`${where}: route belongs on a navigate step.`);
		if (step.condition !== undefined && typeof step.condition !== 'function') problems.push(`${where}: condition must be a function.`);
		if (step.hint !== undefined && !isText(step.hint)) problems.push(`${where}: hint must be text.`);
		if (step.page !== undefined && !isName(step.page)) problems.push(`${where}: page must be a name.`);
		if (step.target !== undefined && step.page === undefined && tour.page === undefined) {
			problems.push(`${where}: a target needs a page, on the step or the tour, so the build can check it exists.`);
		}
	});
	return problems;
}

/**
 * The tour with every default filled in and the whole thing frozen, so a
 * running tour cannot be edited from outside. Call it on a sound definition.
 * @param {Tour} tour
 * @returns {Readonly<Tour>}
 */
export function normalizeTour(tour) {
	const steps = tour.steps.map((step, index) => Object.freeze({
		...step,
		id: step.id === undefined ? `step-${index + 1}` : step.id,
		page: step.page === undefined ? tour.page : step.page,
	}));
	return Object.freeze({ ...tour, steps: Object.freeze(steps) });
}
